import "./filterSection.css";

import {
  MISSION_MONITORING_STATIC_GROUPS,
  NULL_GROUP_LABEL,
} from "../../../constants";
import { capitalize, humanize } from "../../../utils";

import PropTypes from "prop-types";

const labelFor = (group, value, contracts) =>
  group === MISSION_MONITORING_STATIC_GROUPS.CONTRACT
    ? contracts.find(({ id }) => id === value)?.name
    : value ?? NULL_GROUP_LABEL;

// One facet per dimension: a header (name + All/None toggle) over a checklist
// of values, each with the number of sites behind it. OR within a facet, AND
// across facets (see index.js). A single accent colour signals "active";
// everything else stays neutral.
const Facet = ({
  group,
  values,
  selected,
  counts,
  contracts,
  addGroupValue,
  removeGroupValue,
  addAllGroupValues,
  removeAllGroupValues,
}) => {
  const active = selected.length > 0;
  const allSelected = values.length > 0 && selected.length === values.length;

  return (
    <section className={`facet ${active ? "is-active" : ""}`}>
      <div className="facet__head">
        <span className="facet__label-row">
          <span className="facet__name">{humanize(capitalize(group))}</span>
        </span>
        <button
          type="button"
          className="facet__all"
          onClick={() =>
            allSelected ? removeAllGroupValues(group) : addAllGroupValues(group)
          }
        >
          {allSelected ? "None" : "All"}
        </button>
      </div>

      <ul className="facet__list">
        {values.map((value) => {
          const checked = selected.includes(value);
          return (
            <li key={value ?? "null"}>
              {/* Same idiom as the app's other value pickers (CheckOption in
                  react-select): a tinted row with a trailing tick, not a native
                  checkbox. The tick is always rendered and merely hidden, so its
                  column never changes width between states. */}
              <button
                type="button"
                className={`facet__row ${checked ? "is-active" : ""}`}
                aria-pressed={checked}
                onClick={() =>
                  checked
                    ? removeGroupValue(group, value)
                    : addGroupValue(group, value)
                }
              >
                <span
                  className="facet__label"
                  title={labelFor(group, value, contracts)}
                >
                  {labelFor(group, value, contracts)}
                </span>
                <span className="facet__value-count">{counts[value] ?? 0}</span>
                <i
                  className={`fa-solid fa-check facet__tick ${
                    checked ? "" : "is-hidden"
                  }`}
                  aria-hidden="true"
                />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export const FilterSection = ({
  groups,
  groupOptions,
  groupingObject,
  groupValueCounts,
  addGroupValue,
  removeGroupValue,
  clearGroups,
  contracts,
  addAllGroupValues,
  removeAllGroupValues,
}) => {
  const activeGroups = groups.filter(
    (group) => (groupingObject[group] ?? []).length > 0
  );
  // No collapsing at all: the panel already lives behind the accordion header,
  // and having to click a header before seeing a single value made picking a
  // group a three-click affair (R1-18). Every dimension and every value is laid
  // out at once; `.cf-split__aside` is the scrolling ancestor, so the panel
  // scrolls with the rest of the rail instead of capping its own height.

  return (
    <div className="powerdash-component group-by">
      <div className="group-by__head">
        <div
          className={`group-by__head-right ${
            activeGroups.length === 0 ? "is-hidden" : ""
          }`}
        >
          <span className="group-by__active">{activeGroups.length} active</span>
          <button
            type="button"
            className="group-by__clear"
            onClick={clearGroups}
          >
            Clear all
          </button>
        </div>
      </div>

      <div className="group-by__facets">
        {groups.map((group) => (
          <Facet
            key={group}
            group={group}
            values={groupOptions[group] ?? []}
            selected={groupingObject[group] ?? []}
            counts={groupValueCounts?.[group] ?? {}}
            contracts={contracts}
            addGroupValue={addGroupValue}
            removeGroupValue={removeGroupValue}
            addAllGroupValues={addAllGroupValues}
            removeAllGroupValues={removeAllGroupValues}
          />
        ))}
      </div>
    </div>
  );
};

FilterSection.propTypes = {
  groupingObject: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
  groupOptions: PropTypes.object.isRequired,
  groupValueCounts: PropTypes.object,
  addGroupValue: PropTypes.func.isRequired,
  removeGroupValue: PropTypes.func.isRequired,
  clearGroups: PropTypes.func.isRequired,
  contracts: PropTypes.array.isRequired,
  addAllGroupValues: PropTypes.func.isRequired,
  removeAllGroupValues: PropTypes.func.isRequired,
};

Facet.propTypes = {
  group: PropTypes.string.isRequired,
  values: PropTypes.array.isRequired,
  selected: PropTypes.array.isRequired,
  counts: PropTypes.object.isRequired,
  contracts: PropTypes.array.isRequired,
  addGroupValue: PropTypes.func.isRequired,
  removeGroupValue: PropTypes.func.isRequired,
  addAllGroupValues: PropTypes.func.isRequired,
  removeAllGroupValues: PropTypes.func.isRequired,
};
